import { WheelDetail, WheelEvent, WheelSpinRequest } from "../types/wheel-of-luck";

export const WHEEL_SLOT_COUNT = 8;

// Rotate forward from the current angle to the absolute winning slot. Adding
// the target angle directly to the old rotation drifts after the first spin.
export function wheelRotation(current: number, slot: number): number {
  const target = (360 - slot * 45) % 360;
  const previous = ((current % 360) + 360) % 360;
  return current + 5 * 360 + ((target - previous + 360) % 360);
}

export type WheelArt = { wheel: string; pointer: string; sharedCanvas: boolean };

// The built-in art, for a server that sends no skin. Its pointer is cut to its
// own size, so it sits in its own box; a server skin draws both pictures on one
// canvas (sharedCanvas) and they fill the same box.
export const DEFAULT_WHEEL_ART: WheelArt = {
  wheel: "/assets/images/wheel/wheel.svg",
  pointer: "/assets/images/wheel/stick.svg",
  sharedCanvas: false,
};

// A skin's pictures are paths on the Play host that answered (dev on beta, core
// on live), so they resolve against the API's origin, not the client's own.
export function wheelArt(wheel: Pick<WheelEvent, "wheelImage" | "pointerImage"> | null | undefined, apiUrl: string): WheelArt {
  const resolve = (path?: string | null) => {
    if (!path) return undefined;
    if (/^https?:\/\//i.test(path)) return path;
    try {
      return new URL(path.replace(/^\/+/, ""), `${new URL(apiUrl, window.location.href).origin}/`).toString();
    } catch {
      return undefined;
    }
  };
  const wheelImage = resolve(wheel?.wheelImage);
  const pointerImage = resolve(wheel?.pointerImage);
  return wheelImage && pointerImage ? { wheel: wheelImage, pointer: pointerImage, sharedCanvas: true } : DEFAULT_WHEEL_ART;
}

export function isWheelDetail(value: unknown): value is WheelDetail {
  const wheel = value as WheelDetail | null;
  return Boolean(wheel && Number.isInteger(wheel.playerWheelOfLuckId) &&
    Number.isFinite(Date.parse(wheel.serverTime)) &&
    Array.isArray(wheel.slots) && wheel.slots.length === WHEEL_SLOT_COUNT &&
    wheel.slots.every((slot, index) =>
      Number.isInteger(slot.type) && Number.isFinite(slot.quantity) &&
      typeof slot.claimed === "boolean" &&
      (slot.slotIndex === undefined || slot.slotIndex === index)));
}

export function wheelErrorCode(message: string): string {
  return message.split(":", 1)[0].trim();
}

// The server's error codes, each with its translation key in the wheel namespace.
export const wheelErrors: Record<string, string> = {
  NoValidKey: "wheel:errors.noValidKey",
  InsufficientCleg: "wheel:errors.insufficientCleg",
  ClegNotAllowed: "wheel:errors.clegNotAllowed",
  KeyNotAllowed: "wheel:errors.keyNotAllowed",
  AllSlotsClaimed: "wheel:errors.allSlotsClaimed",
  EventEnded: "wheel:errors.eventEnded",
  WheelResetOccurred: "wheel:errors.wheelResetOccurred",
  DuplicateRequest: "wheel:errors.duplicateRequest",
  InvalidPayment: "wheel:errors.invalidPayment",
  WheelNotFound: "wheel:errors.wheelNotFound",
  WheelInactive: "wheel:errors.wheelInactive",
  HistoricalKeyInvalid: "wheel:errors.historicalKeyInvalid",
  InvalidConfiguration: "wheel:errors.invalidConfiguration",
};

export function createSpinRequest(wheelId: number, paymentMethod: 1 | 2): WheelSpinRequest {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return {
    playerWheelOfLuckId: wheelId,
    paymentMethod,
    clientRequestId: Array.from(bytes, b => b.toString(16).padStart(2, "0")).join(""),
  };
}

// The player's language writes the digits; days() words the day count from
// those digits (the translated "{{days}}d").
export type WheelTimeFormat = { language?: string; days?: (days: string) => string };

export function formatWheelTime(target: string | null, serverNow: number, format: WheelTimeFormat = {}): string {
  if (!target || !Number.isFinite(serverNow)) return "—";
  const remaining = Math.max(0, Math.ceil((Date.parse(target) - serverNow) / 1000));
  if (!Number.isFinite(remaining)) return "—";
  const days = Math.floor(remaining / 86400);
  const hours = Math.floor(remaining / 3600) % 24;
  const minutes = Math.floor(remaining / 60) % 60;
  const seconds = remaining % 60;
  const digits = (n: number, width: number) => format.language
    ? n.toLocaleString(format.language, { minimumIntegerDigits: width, useGrouping: false })
    : String(n).padStart(width, "0");
  const dayText = format.days ? format.days(digits(days, 1)) : `${days}d`;
  return `${days ? `${dayText} ` : ""}${[hours, minutes, seconds].map(n => digits(n, 2)).join(":")}`;
}
